import { useEffect, useRef, useState } from 'react';
import SiteLayout, { Stripe } from './SiteLayout';
import { links } from './siteLinks';

// The public contact form. One form with a topic picker replaces Wix's four.
// Who each topic goes to is set in Web Admin and never reaches the browser.
// The hidden "website" field and the time-on-page check are spam traps
// (see /api/site/contact in backend/site_routes.py).

const EMPTY = { topic: '', name: '', email: '', message: '', website: '' };

export default function SiteContact() {
  const [topics, setTopics] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle');  // idle | sending | sent | failed
  const [failure, setFailure] = useState('');
  const openedAt = useRef(0);

  useEffect(() => {
    const previous = document.title;
    document.title = 'Contact · National Hill Climb Association';
    return () => { document.title = previous; };
  }, []);

  useEffect(() => {
    openedAt.current = Date.now();
    let cancelled = false;
    fetch('/api/site/contact-topics')
      .then(res => (res.ok ? res.json() : { topics: [] }))
      .catch(() => ({ topics: [] }))
      .then(d => { if (!cancelled) setTopics(d.topics || []); });
    return () => { cancelled = true; };
  }, []);

  const set = (key) => (e) => setForm(f => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus('sending');
    setErrors({});
    try {
      const res = await fetch('/api/site/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, elapsed: (Date.now() - openedAt.current) / 1000 }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setStatus('sent');
        return;
      }
      setErrors(data.fields || {});
      setFailure(res.status === 429
        ? "You've sent several messages already. Please try again in an hour."
        : (data.error || 'Sorry, something went wrong. Please try again.'));
      setStatus('failed');
    } catch {
      setFailure('Sorry, your message could not be sent. Please check your connection and try again.');
      setStatus('failed');
    }
  };

  return (
    <SiteLayout>
      <section className="site-dark site-page-head">
        <div className="site-wrap site-section-head">
          <div>
            <span className="site-kicker">Get in touch</span>
            <h1 className="site-h2">Contact us</h1>
            <p>Choose a topic and your message goes straight to the right person, who&apos;ll reply by email.</p>
          </div>
        </div>
      </section>
      <Stripe />

      <section className="site-contact">
        <div className="site-wrap site-contact-cols">
          <div className="site-contact-form-wrap">
            {status === 'sent' ? (
              <div className="site-contact-sent" role="status">
                <h2>Thanks, your message has been sent</h2>
                <p>You&apos;ll get a reply by email.</p>
                <button type="button" className="site-btn site-btn--ink" onClick={() => { setForm(EMPTY); setStatus('idle'); openedAt.current = Date.now(); }}>
                  Send another message
                </button>
              </div>
            ) : topics && topics.length === 0 ? (
              <p className="site-contact-note">The contact form is unavailable just now. Please try again later.</p>
            ) : (
              <form className="site-contact-form" onSubmit={submit} noValidate>
                <fieldset className="site-field">
                  <legend>What&apos;s it about?</legend>
                  <div className="site-topics">
                    {(topics || []).map(t => (
                      <label key={t.key} className={`site-topic${form.topic === t.key ? ' site-topic--on' : ''}`}>
                        <input type="radio" name="topic" value={t.key} checked={form.topic === t.key} onChange={set('topic')} />
                        <span className="site-topic-label">{t.label}</span>
                        {t.description && <span className="site-topic-desc">{t.description}</span>}
                      </label>
                    ))}
                  </div>
                  {errors.topic && <span className="site-field-error">{errors.topic}</span>}
                </fieldset>

                <label className="site-field">
                  <span>Your name</span>
                  <input type="text" value={form.name} onChange={set('name')} autoComplete="name" maxLength={100} />
                  {errors.name && <span className="site-field-error">{errors.name}</span>}
                </label>
                <label className="site-field">
                  <span>Your email address</span>
                  <input type="email" value={form.email} onChange={set('email')} autoComplete="email" maxLength={200} />
                  {errors.email && <span className="site-field-error">{errors.email}</span>}
                </label>
                <label className="site-field">
                  <span>Your message</span>
                  <textarea rows={7} value={form.message} onChange={set('message')} maxLength={5000} />
                  {errors.message && <span className="site-field-error">{errors.message}</span>}
                </label>

                {/* Spam trap: hidden from people, filled in by bots. */}
                <label className="site-hp" aria-hidden="true">
                  Website
                  <input type="text" tabIndex={-1} autoComplete="off" value={form.website} onChange={set('website')} />
                </label>

                {status === 'failed' && <p className="site-contact-failed" role="alert">{failure}</p>}
                <button type="submit" className="site-btn site-btn--orange" disabled={status === 'sending'}>
                  {status === 'sending' ? 'Sending…' : 'Send message'}
                </button>
                <p className="site-small-print">
                  We only use your name and email to reply to you. Your message isn&apos;t stored on this site.
                  See the <a href={links.privacy}>privacy statement</a>.
                </p>
              </form>
            )}
          </div>

          <aside className="site-contact-aside">
            <h2>About a particular event?</h2>
            <p>Each event has its own secretary, named on the calendar and in the event&apos;s regulations. They can help with entries, timings and the day itself.</p>
            <a className="site-btn site-btn--ink" href={links.calendar}>See the calendar</a>
          </aside>
        </div>
      </section>
    </SiteLayout>
  );
}
